import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchApi } from '../../utilities/api';
import { ENDPOINTS } from '../../utilities/endpoints';

export interface ExternalAccountV1Response {
  isStripeIntegrated:      boolean;
  areBankDetailsSubmitted: boolean;
}

export interface Payout {
  id: string;
  amount: string;
  currency: string;
  status: string;
  automatic: boolean;
}

export interface BalanceData {
  availableAmount: string;
  pendingAmount?: string;
}

interface PayoutState {
  // existing v1 add
  isAddingV1:    boolean;
  addV1Error:    string | null;
  v1Response:    ExternalAccountV1Response | null;
  // new payout flow
  history:       Payout[];
  balance?:      BalanceData;
  status:        'idle' | 'loading' | 'succeeded' | 'failed';
  error?:        string | null;

  intentData?: PaymentIntentData;
  isFetchingIntent: boolean;
  intentError?: string | null;
}
export interface PaymentIntentData {
  currency: string;
  clientSecret: string;
  paymentIntentId: string;
}
export interface IntentTokenResponse {
  clientSecret: string;
}


const initialState: PayoutState = {
  isAddingV1: false,
  addV1Error: null,
  v1Response: null,

  history: [],
  balance: undefined,
  status: 'idle',
  error: null,

  intentData: undefined,
  isFetchingIntent: false,
  intentError: null,
};

// 1) Add bank/card method (v1)
export const addPayoutMethodV1 = createAsyncThunk<
  ExternalAccountV1Response,
  { accountId: string; tokenId: string },
  { rejectValue: string }
>(
  'payout/addPayoutMethodV1',
  async ({ accountId, tokenId }, { rejectWithValue }) => {
    const auth = localStorage.getItem('token');
    if (!auth) {
      return rejectWithValue('No auth token found');
    }

    try {
      const response = await fetchApi<{ data: ExternalAccountV1Response }>(
        ENDPOINTS.USERS.ADD_BANK_CARD2,
        {
          method: 'POST',
          headers: { Authorization: auth },
          body: JSON.stringify({ accountId, externalAccount: tokenId }),
        }
      );
      return response.data.data;
    } catch (err: any) {
      const msg =
        err.response?.data?.message || err.message || 'Failed to add payout method (v1)';
      return rejectWithValue(msg);
    }
  }
);

// 2) Fetch payout history
export const fetchPayoutDetail = createAsyncThunk<
  Payout[],
  void,
  { rejectValue: string }
>(
  'payout/fetchDetail',
  async (_, { rejectWithValue }) => {
        const auth = localStorage.getItem('token');
    if (!auth) {
      return rejectWithValue('No auth token found');
    }
    try {
      const res = await fetchApi<{ response: { payouts: Payout[] } }>(
        ENDPOINTS.USERS.PAYOUT,
        { method: 'GET' ,
                      headers: { Authorization: auth },

        }
      );
      return res.data.response.payouts;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load payout details');
    }
  }
);

// 3) Fetch current balance
export const fetchBalance = createAsyncThunk<
  BalanceData,
  void,
  { rejectValue: string }
>(
  'payout/fetchBalance',
  async (_, { rejectWithValue }) => {
        const auth = localStorage.getItem('token');
    if (!auth) {
      return rejectWithValue('No auth token found');
    }
    try {
      const res = await fetchApi<{ response: BalanceData }>(
        ENDPOINTS.USERS.BALANCE,
        { method: 'GET' ,
                      headers: { Authorization: auth },

        }
      );
      return res.data.response;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to load balance');
    }
  }
);

// 4) Switch auto/manual payout
export const editPayoutOption = createAsyncThunk<
  void,
  { type: 'automatic' | 'manual' },
  { rejectValue: string }
>(
  'payout/editOption',
  async ({ type }, { rejectWithValue }) => {
        const auth = localStorage.getItem('token');
    if (!auth) {
      return rejectWithValue('No auth token found');
    }
    try {
      await fetchApi(
        ENDPOINTS.USERS.EDIT_PAYOUT,
        { method: 'PUT',
                      headers: { Authorization: auth },
 body: JSON.stringify({ type }) }
      );
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to update payout option');
    }
  }
);

// 5) Trigger instant withdrawal
export const manualPayout = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>(
  'payout/manualPayout',
  async (_, { rejectWithValue }) => {
        const auth = localStorage.getItem('token');
    if (!auth) {
      return rejectWithValue('No auth token found');
    }
    try {
      await fetchApi(
        ENDPOINTS.USERS.MANUAL_PAYOUT,
        { method: 'POST',
                      headers: { Authorization: auth },

         }
      );
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to process manual payout');
    }
  }
);

export const fetchIntentToken = createAsyncThunk<
  PaymentIntentData,
  { amount: number; currency: string; propertyId?: string },
  { rejectValue: string }
>(
  'payout/fetchIntentToken',
  async ({ amount, currency, propertyId }, { rejectWithValue }) => {
    const token = localStorage.getItem('token')
    if (!token) return rejectWithValue('No auth token found')

    try {
      const res = await fetch(ENDPOINTS.USERS.INTENT_TOKEN, {
        method: 'POST',
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount, currency, propertyId }),
      })

      const json = await res.json() as {
        statusCode: number
        message: string
        data?: PaymentIntentData
      }

      if (!res.ok || json.statusCode !== 200 || !json.data) {
        return rejectWithValue(
          json.message ||
          `Intent API error: ${res.statusText}`
        )
      }

      // ✅ Return the data payload (never undefined)
      return json.data
    } catch (err: any) {
      return rejectWithValue(err.message || 'Network error fetching intent')
    }
  }
)


export const instantPayoutToCard = createAsyncThunk<
  void,
  { amount: number; currency?: string; cardId: string; customerId: string },
  { rejectValue: string }
>(
  'payout/instantPayoutToCard',
  async ({ amount, currency = 'usd', cardId, customerId }, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    if (!token) return rejectWithValue('No auth token found');

    try {
      const res = await fetchApi<{ message: string }>(
        ENDPOINTS.USERS.INSTANT_PAYOUT_CARD,
        {
          method: 'POST',
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount, currency, cardId, customerId }),
        }
      );

      if (!res || res.data?.message !== 'Payout successfully completed') {
        return rejectWithValue(res?.data?.message || 'Payout failed');
      }
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || 'Instant payout failed'
      );
    }
  }
);

const payoutSlice = createSlice({
  name: 'payout',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // v1 add
    builder
      .addCase(addPayoutMethodV1.pending, (state) => {
        state.isAddingV1 = true;
        state.addV1Error = null;
      })
      .addCase(
        addPayoutMethodV1.fulfilled,
        (state, action: PayloadAction<ExternalAccountV1Response>) => {
          state.isAddingV1 = false;
          state.v1Response = action.payload;
        }
      )
      .addCase(addPayoutMethodV1.rejected, (state, action) => {
        state.isAddingV1 = false;
        state.addV1Error = action.payload ?? action.error.message ?? 'Unknown error';
      });

    // fetch history
    builder
      .addCase(fetchPayoutDetail.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPayoutDetail.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.history = action.payload;
      })
      .addCase(fetchPayoutDetail.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

    // balance
    builder
      .addCase(fetchBalance.fulfilled, (state, action) => {
        state.balance = action.payload;
      });

    // edit option
    builder
      .addCase(editPayoutOption.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(editPayoutOption.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(editPayoutOption.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

    // manual payout
    builder
      .addCase(manualPayout.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(manualPayout.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(manualPayout.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });

     builder
      .addCase(fetchIntentToken.pending, (s) => {
        s.isFetchingIntent = true
        s.intentError = null
      })
      .addCase(
        fetchIntentToken.fulfilled,
        (s, action: PayloadAction<PaymentIntentData>) => {
          s.isFetchingIntent = false
          s.intentData = action.payload
        }
      )
      .addCase(fetchIntentToken.rejected, (s, action) => {
        s.isFetchingIntent = false
        s.intentError = action.payload ?? action.error.message
      });
      builder
  .addCase(instantPayoutToCard.pending, (state) => {
    state.status = 'loading';
    state.error = null;
  })
  .addCase(instantPayoutToCard.fulfilled, (state) => {
    state.status = 'succeeded';
  })
  .addCase(instantPayoutToCard.rejected, (state, action) => {
    state.status = 'failed';
    state.error = action.payload ?? action.error.message ?? 'Instant payout failed';
  });

  },
});

export default payoutSlice.reducer;